// 23. 10. 2026 (owner, BACKLOG #55, a player's feedback: "I prep a ton of meds and buffs to make a run above my level, get
// great loot as expected, but can't use any of it for 4 more levels"): an item one tier above the survivor's may be worn
// right away (`TIER_WEAR`, `canWear` in content/items.ts), with the stats of the top of the survivor's tier until the
// survivor reaches the item's tier; from that level its full stats. "The top of the survivor's tier": the item as if it
// were an item of that tier at the tier's highest item level, with the same rolls (the tier upgrade's math, items/tierUp.ts,
// going down): the headline stat by the catalog's means, every affix at the same place in that tier's range, a Pristine
// affix 1.3× that tier's best. Rarity, perk, +N and milestone picks stay. Nothing is saved: the stats come from the item
// and the survivor's level each time, in `deriveStats` (formulas.ts) for everything that fights or counts Power (runs, PvP,
// the Pit, the horde, Equip best, the simulations) and in `itemPowerAt` for an item's PWR as this survivor wears it.

import { TIER_LEVELS, TIER_WEAR, itemTier } from '../content/items';
import type { Equipment, GearSlot, ItemInstance } from '../types';
import { tierUpAffixes, tierUpBase } from './tierUp';
import { wearableTier } from './upgrade';

/**
 * The level the item has its full stats from, while a survivor of `level` wears it capped (an item above the survivor's
 * tier); null when it is worn as it is (its tier or lower, or the rule off).
 */
export function growsAt(item: { templateId: string; tier?: number | null }, level: number): number | null {
  if (!TIER_WEAR.on) return null;
  const own = itemTier(item);
  return own > wearableTier(level) ? TIER_LEVELS[own][0] : null;
}

/**
 * The item as a survivor of `level` wears it: an item above the survivor's tier as an item of the survivor's tier at that
 * tier's highest item level, with the same rolls (`tierUpBase`, `tierUpAffixes`); +N, milestone picks, rarity, perk and
 * durability stay. The same object when nothing changes (its tier or lower, or `TIER_WEAR` off: the rule before).
 */
export function wornAs<T extends ItemInstance>(item: T, level: number): T {
  if (!TIER_WEAR.on) return item;
  const top = wearableTier(level);
  if (itemTier(item) <= top) return item;
  const ilvl = TIER_LEVELS[top][1];
  return { ...item, tier: top, ilvl, base: tierUpBase(item, top), affixes: tierUpAffixes(item, top, ilvl) };
}

/** Worn gear as a survivor of `level` wears it (`wornAs` on every piece); the same object when nothing changes. */
export function wornEquipment(equipment: Equipment, level: number): Equipment {
  if (!TIER_WEAR.on) return equipment;
  let out: Equipment | null = null;
  for (const [slot, it] of Object.entries(equipment) as [GearSlot, ItemInstance | null | undefined][]) {
    if (!it) continue;
    const w = wornAs(it, level);
    if (w !== it) (out ??= { ...equipment })[slot] = w;
  }
  return out ?? equipment;
}
